import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Row, 
  Col, 
  Form, 
  Alert, 
  Spinner,
  Badge,
  Accordion,
  Modal
} from 'react-bootstrap';
import swal from 'sweetalert';

// Platform icons (using text fallbacks)
const PlatformIcon = ({ platform }) => {
  const icons = {
    youtube: <span style={{ color: '#FF0000' }}>▶</span>,
    facebook: <span style={{ color: '#1877F2' }}>f</span>,
    twitch: <span style={{ color: '#9146FF' }}>T</span>
  };
  return icons[platform] || <span>●</span>;
};

const SocialStreamingConfig = ({ streamId, streamName }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [socialConfig, setSocialConfig] = useState({
    youtube: { enabled: false, rtmpUrl: '', streamKey: '', streamName: '' },
    facebook: { enabled: false, rtmpUrl: '', streamKey: '', serverUrl: '' },
    twitch: { enabled: false, rtmpUrl: '', streamKey: '', server: 'live.twitch.tv' },
    custom: []
  });
  const [testingPlatform, setTestingPlatform] = useState(null);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [newCustomPlatform, setNewCustomPlatform] = useState({ name: '', rtmpUrl: '', enabled: true });

  // Load social config on mount
  useEffect(() => {
    if (streamId) {
      fetchSocialConfig();
    }
  }, [streamId]);

  const fetchSocialConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/streams/${streamId}/social-config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSocialConfig(data.data);
      }
    } catch (error) {
      console.error('Error fetching social config:', error);
      swal('Error', 'Failed to load social streaming configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (platform, field, value) => {
    setSocialConfig(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/streams/${streamId}/social-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(socialConfig)
      });
      const data = await response.json();
      if (data.success) {
        swal('Success', 'Social streaming configuration saved!', 'success');
      } else {
        swal('Error', data.error || 'Failed to save configuration', 'error');
      }
    } catch (error) {
      console.error('Error saving social config:', error);
      swal('Error', 'Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (platform) => {
    setTestingPlatform(platform);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/streams/${streamId}/social-config/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ platform })
      });
      const data = await response.json();
      if (data.success) {
        swal({
          title: 'Configuration Ready',
          text: `${platform.charAt(0).toUpperCase() + platform.slice(1)} is configured and ready to receive streams.\n\nRTMP URL: ${data.data.rtmpUrl}`,
          icon: 'success'
        });
      } else {
        swal('Error', data.error || 'Failed to test connection', 'error');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      swal('Error', 'Failed to test connection', 'error');
    } finally {
      setTestingPlatform(null);
    }
  };

  const addCustomPlatform = () => {
    if (!newCustomPlatform.name || !newCustomPlatform.rtmpUrl) {
      swal('Error', 'Please enter platform name and RTMP URL', 'error');
      return;
    }
    setSocialConfig(prev => ({
      ...prev,
      custom: [...prev.custom, { ...newCustomPlatform }]
    }));
    setNewCustomPlatform({ name: '', rtmpUrl: '', enabled: true });
    setShowAddCustom(false);
  };

  const removeCustomPlatform = (index) => {
    setSocialConfig(prev => ({
      ...prev,
      custom: prev.custom.filter((_, i) => i !== index)
    }));
  };

  const toggleCustomPlatform = (index) => {
    setSocialConfig(prev => ({
      ...prev,
      custom: prev.custom.map((p, i) => 
        i === index ? { ...p, enabled: !p.enabled } : p
      )
    }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading social streaming configuration...</p>
      </div>
    );
  }

  return (
    <div>
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-share-alt me-2"></i>
            Social Streaming Configuration
          </h5>
          <Badge bg="info">Multi-Platform Broadcasting</Badge>
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-4">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Simulcast to Multiple Platforms:</strong> Configure RTMP destinations for YouTube, Facebook, and Twitch. 
            When you start streaming, your broadcast will be pushed to all enabled platforms simultaneously.
          </Alert>

          <Accordion defaultActiveKey={['youtube', 'facebook', 'twitch']} alwaysOpen>
            {/* YouTube */}
            <Accordion.Item eventKey="youtube">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <PlatformIcon platform="youtube" />
                  <span className="ms-2 me-2">YouTube</span>
                  {socialConfig.youtube.enabled ? (
                    <Badge bg="success">Enabled</Badge>
                  ) : (
                    <Badge bg="secondary">Disabled</Badge>
                  )}
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <Form.Check
                  type="switch"
                  id="youtube-enabled"
                  label="Enable YouTube Streaming"
                  checked={socialConfig.youtube.enabled}
                  onChange={(e) => handleChange('youtube', 'enabled', e.target.checked)}
                  className="mb-3"
                />
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>RTMP Ingestion Server URL</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="rtmp://a.rtmp.youtube.com/live2"
                        value={socialConfig.youtube.rtmpUrl}
                        onChange={(e) => handleChange('youtube', 'rtmpUrl', e.target.value)}
                        disabled={!socialConfig.youtube.enabled}
                      />
                      <Form.Text className="text-muted">
                        Get this from YouTube Studio → Live Streaming → Stream setup
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stream Key</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Your YouTube stream key"
                        value={socialConfig.youtube.streamKey}
                        onChange={(e) => handleChange('youtube', 'streamKey', e.target.value)}
                        disabled={!socialConfig.youtube.enabled}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Stream Name (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., My Live Stream"
                    value={socialConfig.youtube.streamName}
                    onChange={(e) => handleChange('youtube', 'streamName', e.target.value)}
                    disabled={!socialConfig.youtube.enabled}
                  />
                </Form.Group>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleTestConnection('youtube')}
                  disabled={!socialConfig.youtube.enabled || testingPlatform === 'youtube'}
                >
                  {testingPlatform === 'youtube' ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="fas fa-plug me-1"></i>
                  )}
                  Test Connection
                </Button>
              </Accordion.Body>
            </Accordion.Item>

            {/* Facebook */}
            <Accordion.Item eventKey="facebook">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <PlatformIcon platform="facebook" />
                  <span className="ms-2 me-2">Facebook</span>
                  {socialConfig.facebook.enabled ? (
                    <Badge bg="success">Enabled</Badge>
                  ) : (
                    <Badge bg="secondary">Disabled</Badge>
                  )}
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <Form.Check
                  type="switch"
                  id="facebook-enabled"
                  label="Enable Facebook Streaming"
                  checked={socialConfig.facebook.enabled}
                  onChange={(e) => handleChange('facebook', 'enabled', e.target.checked)}
                  className="mb-3"
                />
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Server URL</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="rtmp://live-api-s.facebook.com:443/rtmp"
                        value={socialConfig.facebook.serverUrl}
                        onChange={(e) => handleChange('facebook', 'serverUrl', e.target.value)}
                        disabled={!socialConfig.facebook.enabled}
                      />
                      <Form.Text className="text-muted">
                        Get this from Facebook Live Producer → Stream Setup
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stream Key</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Your Facebook stream key"
                        value={socialConfig.facebook.streamKey}
                        onChange={(e) => handleChange('facebook', 'streamKey', e.target.value)}
                        disabled={!socialConfig.facebook.enabled}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleTestConnection('facebook')}
                  disabled={!socialConfig.facebook.enabled || testingPlatform === 'facebook'}
                >
                  {testingPlatform === 'facebook' ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="fas fa-plug me-1"></i>
                  )}
                  Test Connection
                </Button>
              </Accordion.Body>
            </Accordion.Item>

            {/* Twitch */}
            <Accordion.Item eventKey="twitch">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <PlatformIcon platform="twitch" />
                  <span className="ms-2 me-2">Twitch</span>
                  {socialConfig.twitch.enabled ? (
                    <Badge bg="success">Enabled</Badge>
                  ) : (
                    <Badge bg="secondary">Disabled</Badge>
                  )}
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <Form.Check
                  type="switch"
                  id="twitch-enabled"
                  label="Enable Twitch Streaming"
                  checked={socialConfig.twitch.enabled}
                  onChange={(e) => handleChange('twitch', 'enabled', e.target.checked)}
                  className="mb-3"
                />
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Twitch Server</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="live.twitch.tv"
                        value={socialConfig.twitch.server}
                        onChange={(e) => handleChange('twitch', 'server', e.target.value)}
                        disabled={!socialConfig.twitch.enabled}
                      />
                      <Form.Text className="text-muted">
                        Usually: live.twitch.tv
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stream Key</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Your Twitch stream key"
                        value={socialConfig.twitch.streamKey}
                        onChange={(e) => handleChange('twitch', 'streamKey', e.target.value)}
                        disabled={!socialConfig.twitch.enabled}
                      />
                      <Form.Text className="text-muted">
                        Get this from Twitch Dashboard → Settings → Stream
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleTestConnection('twitch')}
                  disabled={!socialConfig.twitch.enabled || testingPlatform === 'twitch'}
                >
                  {testingPlatform === 'twitch' ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="fas fa-plug me-1"></i>
                  )}
                  Test Connection
                </Button>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Custom Platforms */}
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <i className="fas fa-plug me-2"></i>
                Custom RTMP Destinations
              </h6>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => setShowAddCustom(true)}
              >
                <i className="fas fa-plus me-1"></i>
                Add Custom Platform
              </Button>
            </div>
            
            {socialConfig.custom && socialConfig.custom.length > 0 ? (
              <div className="custom-platforms-list">
                {socialConfig.custom.map((platform, index) => (
                  <Card key={index} className="mb-2">
                    <Card.Body className="py-2">
                      <Row className="align-items-center">
                        <Col md={3}>
                          <Form.Check
                            type="switch"
                            id={`custom-${index}-enabled`}
                            checked={platform.enabled}
                            onChange={() => toggleCustomPlatform(index)}
                            label={platform.name}
                          />
                        </Col>
                        <Col md={7}>
                          <small className="text-muted">{platform.rtmpUrl}</small>
                        </Col>
                        <Col md={2} className="text-end">
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => removeCustomPlatform(index)}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            ) : (
              <Alert variant="secondary" className="mb-0">
                No custom RTMP destinations configured. Click "Add Custom Platform" to add more.
              </Alert>
            )}
          </div>
        </Card.Body>
        <Card.Footer>
          <div className="d-flex justify-content-between">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Changes are saved immediately to your stream configuration.
            </small>
            <Button 
              variant="primary" 
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </Card.Footer>
      </Card>

      {/* Add Custom Platform Modal */}
      <Modal show={showAddCustom} onHide={() => setShowAddCustom(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Custom RTMP Destination</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Platform Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Vimeo, Custom CDN"
              value={newCustomPlatform.name}
              onChange={(e) => setNewCustomPlatform({ ...newCustomPlatform, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>RTMP URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="rtmp://your-server.com/live/stream-key"
              value={newCustomPlatform.rtmpUrl}
              onChange={(e) => setNewCustomPlatform({ ...newCustomPlatform, rtmpUrl: e.target.value })}
            />
            <Form.Text className="text-muted">
              Include the full RTMP URL with stream key appended
            </Form.Text>
          </Form.Group>
          <Form.Check
            type="switch"
            id="new-custom-enabled"
            label="Enable this destination"
            checked={newCustomPlatform.enabled}
            onChange={(e) => setNewCustomPlatform({ ...newCustomPlatform, enabled: e.target.checked })}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddCustom(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={addCustomPlatform}>
            Add Platform
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SocialStreamingConfig;

